/* ============================================================================
   AQUA PLUS — hero.js
   The home HeroStack, v2 gesture engine.

   Drag: the active slide tracks the finger 1:1 on the locked axis while the
   neighbor slide peeks in alongside it; release either commits (continuing
   from the current position with a velocity-aware duration) or snaps back
   softly. Edges get elastic resistance. Wheel / arrow keys / tabs / dots run
   the app's 750ms expressive transition.
   ============================================================================ */
(function () {
  'use strict';

  var stage = document.querySelector('[data-hero-stack]');
  if (!stage) return;

  var slides = Array.prototype.slice.call(stage.querySelectorAll('[data-hero-slide]'));
  if (slides.length === 0) return;

  var dotsRail = stage.querySelector('[data-hero-dots]');
  var counterCurrent = stage.querySelector('[data-hero-counter-current]');
  var counterTotal = stage.querySelector('[data-hero-counter-total]');
  var tabs = Array.prototype.slice.call(stage.querySelectorAll('[data-hero-tab]'));

  var EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';         // programmatic nav — the app's expressive curve
  var SETTLE_EASE = 'cubic-bezier(0.33, 1, 0.68, 1)'; // drag release — easeOutCubic: starts near finger speed, glides out
  var NAV_MS = 750;           // programmatic transitions (wheel/keys/tabs/dots)
  var SNAP_MS = 500;          // cancelled drag snap-back
  var SETTLE_SLOPE = 3;       // initial-velocity multiplier of SETTLE_EASE (1/x1)
  var SETTLE_MIN_MS = 400;    // fastest allowed drag-commit settle
  var SETTLE_MAX_MS = 850;    // slow releases glide the rest of the way unhurried
  var VELOCITY_FLOOR = 500;   // px/s assumed for near-still releases
  var COMMIT_FRACTION = 0.22; // dragged fraction of the stage that commits
  var COMMIT_VELOCITY = 450;  // px/s flick that commits regardless of distance
  var AXIS_LOCK_PX = 8;       // movement before the drag locks to an axis
  var EDGE_RESISTANCE = 0.28; // elastic damping at the first/last slide
  var WHEEL_COOLDOWN = 700;

  /* Group slides by category, preserving block order */
  var categories = [];
  var byCategory = {};
  slides.forEach(function (el) {
    var cat = el.getAttribute('data-category');
    if (!byCategory[cat]) {
      byCategory[cat] = [];
      categories.push(cat);
    }
    byCategory[cat].push(el);
  });

  var catIdx = 0;
  var slideIdx = 0;
  var animating = false;
  var lastWheelAt = 0;
  var suppressClickUntil = 0;

  function activeSlides() {
    return byCategory[categories[catIdx]] || [];
  }

  function activeSlide() {
    return activeSlides()[slideIdx];
  }

  function pad(n) {
    return String(n).padStart(2, '0');
  }

  /* The app kept 200ms slide animations under prefers-reduced-motion (its
     framer transitions weren't touched by the global CSS kill switch), so we
     cap rather than zero. Inline !important beats base.css's
     `transition-duration: 0ms !important` reduced-motion rule. */
  var REDUCED = window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function dur(ms) {
    return REDUCED ? Math.min(ms, 200) : ms;
  }

  function setTransition(el, ms, ease) {
    var curve = ease || EASE;
    var d = dur(ms);
    el.style.setProperty(
      'transition',
      'transform ' + d + 'ms ' + curve + ', opacity ' + d + 'ms ' + curve,
      'important'
    );
  }

  function freeze(el) {
    el.style.setProperty('transition', 'none', 'important');
  }

  function deactivate(el) {
    if (!el) return;
    el.classList.remove('is-active', 'is-peek', 'no-content-anim');
    freeze(el);
    el.style.transform = '';
    el.style.opacity = '';
    requestAnimationFrame(function () {
      el.style.removeProperty('transition');
    });
  }

  function paintChrome() {
    var list = activeSlides();

    if (counterCurrent) counterCurrent.textContent = pad(slideIdx + 1);
    if (counterTotal) counterTotal.textContent = pad(list.length);

    if (dotsRail) {
      dotsRail.innerHTML = '';
      list.forEach(function (_, i) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'hero-stack__dot' + (i === slideIdx ? ' is-active' : '');
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-selected', i === slideIdx ? 'true' : 'false');
        dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        dot.addEventListener('click', function () { jumpToSlide(i); });
        dotsRail.appendChild(dot);
      });
    }

    tabs.forEach(function (tab) {
      var selected = tab.getAttribute('data-hero-tab') === categories[catIdx];
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      if (selected && tab.parentElement) {
        var nav = tab.parentElement;
        var target = tab.offsetLeft - nav.clientWidth / 2 + tab.clientWidth / 2;
        nav.scrollTo({ left: target, behavior: 'smooth' });
      }
    });
  }

  /* ---- Programmatic transitions (wheel / keys / tabs / dots) -------------- */

  function transitionSlides(incoming, outgoing, axis, dir) {
    if (!incoming || incoming === outgoing) return;
    animating = true;

    var enterX = axis === 'x' ? (dir > 0 ? '100%' : '-100%') : '0';
    var enterY = axis === 'y' ? (dir > 0 ? '100%' : '-100%') : '0';
    var exitX = axis === 'x' ? (dir > 0 ? '-100%' : '100%') : '0';
    var exitY = axis === 'y' ? (dir > 0 ? '-100%' : '100%') : '0';

    freeze(incoming);
    incoming.style.transform = 'translate(' + enterX + ',' + enterY + ')';
    incoming.style.opacity = '0.6';
    incoming.classList.add('is-active');
    void incoming.getBoundingClientRect();
    setTransition(incoming, NAV_MS);
    incoming.style.transform = 'translate(0,0)';
    incoming.style.opacity = '1';

    if (outgoing) {
      setTransition(outgoing, NAV_MS);
      outgoing.style.transform = 'translate(' + exitX + ',' + exitY + ')';
      outgoing.style.opacity = '0.6';
    }

    window.setTimeout(function () {
      deactivate(outgoing);
      animating = false;
    }, dur(NAV_MS) + 20);
  }

  function changeSlide(dir) {
    if (animating) return;
    var list = activeSlides();
    var next = slideIdx + dir;
    if (next < 0 || next >= list.length) return;
    var outgoing = activeSlide();
    slideIdx = next;
    transitionSlides(activeSlide(), outgoing, 'y', dir);
    paintChrome();
  }

  function changeCategory(dir) {
    if (animating) return;
    var next = catIdx + dir;
    if (next < 0 || next >= categories.length) return;
    var outgoing = activeSlide();
    catIdx = next;
    slideIdx = 0;
    transitionSlides(activeSlide(), outgoing, 'x', dir);
    paintChrome();
  }

  function jumpToCategory(handle) {
    var target = categories.indexOf(handle);
    if (target < 0 || target === catIdx || animating) return;
    var dir = target > catIdx ? 1 : -1;
    var outgoing = activeSlide();
    catIdx = target;
    slideIdx = 0;
    transitionSlides(activeSlide(), outgoing, 'x', dir);
    paintChrome();
  }

  function jumpToSlide(target) {
    if (target === slideIdx || animating) return;
    var dir = target > slideIdx ? 1 : -1;
    var outgoing = activeSlide();
    slideIdx = target;
    transitionSlides(activeSlide(), outgoing, 'y', dir);
    paintChrome();
  }

  /* ---- Tabs / keyboard / wheel -------------------------------------------- */

  tabs.forEach(function (tab) {
    tab.addEventListener('click', function () {
      jumpToCategory(tab.getAttribute('data-hero-tab'));
    });
  });

  window.addEventListener('keydown', function (e) {
    if (e.key === 'ArrowDown' || e.key === 'PageDown' || e.key === ' ') {
      e.preventDefault();
      changeSlide(1);
    } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
      e.preventDefault();
      changeSlide(-1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      changeCategory(1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      changeCategory(-1);
    }
  });

  stage.addEventListener('wheel', function (e) {
    e.preventDefault();
    var now = Date.now();
    if (now - lastWheelAt < WHEEL_COOLDOWN) return;
    if (Math.abs(e.deltaY) < 20) return;
    lastWheelAt = now;
    changeSlide(e.deltaY > 0 ? 1 : -1);
  }, { passive: false });

  /* ---- Drag engine: 1:1 tracking with a peeking neighbor ------------------ */

  var drag = null;
  var rafPending = false;

  /* sign < 0 (finger moved up/left) → next; sign > 0 → previous */
  function neighborFor(axis, sign) {
    if (axis === 'y') {
      var list = activeSlides();
      var target = slideIdx + (sign < 0 ? 1 : -1);
      if (target < 0 || target >= list.length) return null;
      return { el: list[target], toCat: catIdx, toSlide: target };
    }
    var targetCat = catIdx + (sign < 0 ? 1 : -1);
    if (targetCat < 0 || targetCat >= categories.length) return null;
    return { el: byCategory[categories[targetCat]][0], toCat: targetCat, toSlide: 0 };
  }

  stage.addEventListener('pointerdown', function (e) {
    if (animating || drag) return;
    if (e.target.closest('a, button')) return;
    drag = {
      id: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      x: e.clientX,
      y: e.clientY,
      axis: null,
      sign: 0,
      neighbor: null,
      w: stage.clientWidth || 1,
      h: stage.clientHeight || 1,
      moved: false,
      samples: [{ t: performance.now(), x: e.clientX, y: e.clientY }]
    };
    try { stage.setPointerCapture(e.pointerId); } catch (err) { /* noop */ }
  });

  stage.addEventListener('pointermove', function (e) {
    if (!drag || e.pointerId !== drag.id) return;
    drag.x = e.clientX;
    drag.y = e.clientY;
    drag.samples.push({ t: performance.now(), x: e.clientX, y: e.clientY });
    if (drag.samples.length > 6) drag.samples.shift();
    if (!rafPending) {
      rafPending = true;
      requestAnimationFrame(applyDrag);
    }
  });

  function applyDrag() {
    rafPending = false;
    if (!drag) return;

    var dx = drag.x - drag.startX;
    var dy = drag.y - drag.startY;

    if (!drag.axis) {
      if (Math.abs(dx) < AXIS_LOCK_PX && Math.abs(dy) < AXIS_LOCK_PX) return;
      drag.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
    }
    drag.moved = true;

    var o = drag.axis === 'x' ? dx : dy;
    var sign = o === 0 ? drag.sign : (o < 0 ? -1 : 1);

    if (sign !== drag.sign) {
      /* direction flipped mid-drag — swap the peeking neighbor */
      if (drag.neighbor) deactivate(drag.neighbor.el);
      drag.sign = sign;
      drag.neighbor = sign === 0 ? null : neighborFor(drag.axis, sign);
      if (drag.neighbor) {
        drag.neighbor.el.classList.add('is-peek', 'no-content-anim');
        freeze(drag.neighbor.el);
      }
    }

    var current = activeSlide();
    if (!current) return;
    freeze(current);

    if (!drag.neighbor) {
      /* edge of the stack — elastic resistance, nothing peeks */
      var damped = o * EDGE_RESISTANCE;
      current.style.transform = drag.axis === 'x'
        ? 'translateX(' + damped + 'px)'
        : 'translateY(' + damped + 'px)';
      return;
    }

    var size = drag.axis === 'x' ? drag.w : drag.h;
    var base = sign < 0 ? size : -size;
    var nEl = drag.neighbor.el;
    if (drag.axis === 'x') {
      current.style.transform = 'translateX(' + o + 'px)';
      nEl.style.transform = 'translateX(' + (base + o) + 'px)';
    } else {
      current.style.transform = 'translateY(' + o + 'px)';
      nEl.style.transform = 'translateY(' + (base + o) + 'px)';
    }
  }

  function endDrag(e) {
    if (!drag || e.pointerId !== drag.id) return;
    var d = drag;
    drag = null;
    try { stage.releasePointerCapture(e.pointerId); } catch (err) { /* noop */ }

    var current = activeSlide();

    if (!d.moved) {
      /* clean tap → open the active category (as in the app) */
      if (e.type === 'pointerup' && current) {
        var url = current.getAttribute('data-url');
        if (url) window.location.href = url;
      }
      return;
    }

    suppressClickUntil = Date.now() + 400;

    var dx = d.x - d.startX;
    var dy = d.y - d.startY;
    var o = d.axis === 'x' ? dx : dy;
    var size = d.axis === 'x' ? d.w : d.h;

    /* velocity from the last ~60–120ms of movement */
    var samples = d.samples;
    var last = samples[samples.length - 1];
    var ref = samples[0];
    for (var i = samples.length - 1; i >= 0; i--) {
      if (last.t - samples[i].t >= 60) {
        ref = samples[i];
        break;
      }
    }
    var dt = Math.max(last.t - ref.t, 1);
    var v = ((d.axis === 'x' ? last.x - ref.x : last.y - ref.y) / dt) * 1000;

    var fastReturn = Math.abs(v) > COMMIT_VELOCITY && Math.sign(v) !== d.sign;
    var commit = d.neighbor && !fastReturn && Math.sign(o) === d.sign &&
      (Math.abs(o) > size * COMMIT_FRACTION || Math.abs(v) > COMMIT_VELOCITY);

    if (commit) {
      animating = true;
      var nEl = d.neighbor.el;
      /* Settle time matched to hand speed: the curve's initial velocity is
         SETTLE_SLOPE × remaining/duration, so duration = slope·R/v makes the
         animation pick up close to the finger's velocity and decelerate. */
      var remaining = Math.max(size - Math.abs(o), 1);
      var speed = Math.max(Math.abs(v), VELOCITY_FLOOR);
      var ms = dur(Math.round(Math.min(SETTLE_MAX_MS,
        Math.max(SETTLE_MIN_MS, (SETTLE_SLOPE * remaining / speed) * 1000))));
      setTransition(current, ms, SETTLE_EASE);
      setTransition(nEl, ms, SETTLE_EASE);
      var out = d.sign < 0 ? '-100%' : '100%';
      if (d.axis === 'x') {
        current.style.transform = 'translateX(' + out + ')';
        nEl.style.transform = 'translateX(0)';
      } else {
        current.style.transform = 'translateY(' + out + ')';
        nEl.style.transform = 'translateY(0)';
      }
      var toCat = d.neighbor.toCat;
      var toSlide = d.neighbor.toSlide;
      window.setTimeout(function () {
        catIdx = toCat;
        slideIdx = toSlide;
        nEl.classList.remove('is-peek');
        nEl.classList.add('is-active'); /* keeps no-content-anim — no replay flash */
        deactivate(current);
        paintChrome();
        animating = false;
      }, ms + 20);
    } else {
      /* soft snap back */
      animating = true;
      if (current) {
        setTransition(current, SNAP_MS, SETTLE_EASE);
        current.style.transform = 'translate(0,0)';
      }
      var nb = d.neighbor;
      if (nb) {
        var back = d.sign < 0 ? '100%' : '-100%';
        setTransition(nb.el, SNAP_MS, SETTLE_EASE);
        nb.el.style.transform = d.axis === 'x'
          ? 'translateX(' + back + ')'
          : 'translateY(' + back + ')';
      }
      window.setTimeout(function () {
        if (nb) deactivate(nb.el);
        if (current) {
          freeze(current);
          current.style.transform = '';
          current.style.opacity = '';
          requestAnimationFrame(function () { current.style.removeProperty('transition'); });
        }
        animating = false;
      }, dur(SNAP_MS) + 20);
    }
  }

  stage.addEventListener('pointerup', endDrag);
  stage.addEventListener('pointercancel', endDrag);

  /* A drag must never fire the links underneath it */
  stage.addEventListener('click', function (e) {
    if (Date.now() < suppressClickUntil) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, true);

  /* ---- Init ---------------------------------------------------------------- */

  var first = activeSlide();
  if (first) first.classList.add('is-active');
  paintChrome();
})();
